import { Theme } from "@mui/material";
import { enumKeyOf, enumKeys } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import Document from "documents/entities/document/document";
import {
  DocumentError
} from "documents/entities/document/store/documents-redux-slice";
import UnavailableDocument from "documents/values/unavailable-document";
import EntityAgreement from "legal-entities/entities/entity-agreement/entity-agreement";
import {
  EntityAgreementError
} from "legal-entities/entities/entity-agreement/store/entity-agreements-redux-slice";
import Forum from "messaging/entities/forum/forum";
import {
  ForumError
} from "messaging/entities/forum/store/forums-redux-slice";
import Permission from "permissions/entities/permission/permission";
import {
  AccessLevel,
  ChildResourceType,
  Resource,
  ResourceConstructor,
  ResourceType,
  Scope
} from "permissions/entities/permission/resources/general";
import {
  EntityAgreementAccess,
  LegalEntityAccess
} from 'permissions/entities/permission/resources/legal-entities-resources';
import { 
  AccessTypeEnumByResourceType, 
  CardMeta, 
  CardStyle, 
  CATEGORY_OPTIONS, 
  CategoryLabel, 
  ChildResourceConstructorMap, 
  ControlMeta, 
  MetaControlType, 
  TableRow
} from "permissions/entities/permission/view/components/permissions-table/types";
import UserEntityInfo from "users/values/user-entity-info/user-entity-info";
import BarebonesProposalInfoAPIResponse from "work/entities/proposal/api/response-contracts/barebones-proposal-info-api-response";
import {
  ProposalError
} from "work/entities/proposal/store/proposals-redux-slice";
import BarebonesWorkAgreementInfoAPIResponse from "work/entities/work-agreement/api/request-contracts/barebones-work-agreement-api-response";
import {
  WorkAgreementError
} from "work/entities/work-agreement/store/work-agreement-redux-slice";

import {
  brown
} from '@mui/material/colors';
import { alpha } from "@mui/material/styles";
import Individual from "marketplace/entities/individual/individual";
import { DocumentAccess, DocumentContentsAccess } from "permissions/entities/permission/resources/documents-resources";
import { ForumAccess } from "permissions/entities/permission/resources/messaging-resources";
import { ProposalAccess, WorkAgreementAccess } from "permissions/entities/permission/resources/work-resources";
import Session from "users/session/session";
import { PermissionsByGranteeId } from "permissions/entities/permission/api/permissions-api-service";
import PaginatedResponse from "common/contracts/paginated-response";
import PermissionTemplate from "permissions/entities/permission/permission-template";

export function getResourceBaseColor(resource?: Resource): string {
  if (!resource) return brown[800];
  const t = resource.resourceType;
  const match = CATEGORY_OPTIONS.find(o => o.regex.test(ResourceType[t]));
  return match?.color ?? brown[800];
}

export function getCardStyle(
  theme: Theme,
  resource: Resource,
  disabled: boolean,
  activeCategory: CategoryLabel | ''
): CardStyle {
  const base = disabled ? theme.palette.grey[500] : getResourceBaseColor(resource);
  const border = alpha(base, 0.75);
  const matchesActiveCategory: boolean = CATEGORY_OPTIONS.find(o =>
    o.regex.test(ResourceType[resource.resourceType]))?.label === activeCategory;
  return {
    baseColor: base,
    textColor: base,
    bgColor: alpha(base, 0.05),
    borderColor: border,
    display: (matchesActiveCategory || !activeCategory) ? 'flex' : 'none',
    selectSx: {
      '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(border, 0.5) },
      '& .MuiSelect-outlined': { backgroundColor: alpha(base, 0.05) }
    }
  };
}

export const selectEffectivePermission = (perms: Permission[]): Permission | undefined => {
  if (!perms.length) return undefined;

  const rankAccess = (p: Permission): number => {
    if (!('accessLevel' in p.resource)) return 0;
    const v = (p.resource as { accessLevel: AccessLevel | string | number | undefined }).accessLevel;
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    const key = enumKeyOf(AccessLevel, v);
    const idx = enumKeys(AccessLevel).indexOf(key);
    return Math.max(idx, 0);
  };
  const enabled: Permission[] = perms.filter(p => !p.disabledDate);
  const pool: Permission[] = enabled.length ? enabled : perms;

  pool.sort((a, b) => rankAccess(b) - rankAccess(a));

  const getTimestamp = (p: Permission): number => p.createdDate?.value.unix() ?? 0;
  pool.sort((a, b) => {
    const r = rankAccess(b) - rankAccess(a);
    if (r !== 0) return r;
    return getTimestamp(b) - getTimestamp(a);
  });

  return pool[0];
};

export const makeVirtualPermission = (granteeId: Guid, resource: Resource): Permission =>
  new Permission(resource, granteeId, Guid.empty);

/**
 * @description Get class name from resource
 * @param resource - The resource constructor
 * @returns The class name of the resource
 */
export const classNameFromResource = (resource: ResourceConstructor): string =>
  resource.name.replaceAll(/([A-Z])/g, ' $1').replaceAll(/Access$/g, '').trim();

export const isTopLevelParent = (p: Permission) =>
  !p.parentId &&
  !('documentId' in p.resource && p.resource.documentId) &&
  !('forumId' in p.resource && p.resource.forumId) &&
  !('proposalId' in p.resource && p.resource.proposalId) &&
  !('templateId' in p.resource && p.resource.templateId);

function loadName(
  id: Guid | undefined,
  { isLoading, error, pick }: {
    isLoading: (id: Guid) => boolean;
    error: (id: Guid) => string | null;
    pick: (id: Guid) => string | undefined
  },
  fallback: string,
): string {
  if (!id) return fallback;
  if (isLoading(id)) return '(Loading...)';
  const err = error(id);
  if (err) return `(Error: ${err})`;
  return pick(id) ?? fallback;
}

/**
 * Get the name of the user who granted the permission.
 * @param permission The permission object.
 * @param individuals List of individuals to look up the grantor's name.
 * @param loadingIndividualUserIds List of user IDs that are currently loading.
 * @returns The name of the grantor user or a placeholder if loading or unknown.
 */
export const getGrantorName = (
  permission: Permission,
  individuals: Individual[],
  loadingIndividualUserIds: Guid[]
): string => {
  if (!permission.grantorUserId) return 'Unknown User';
  if (permission.grantorUserId.isEqualTo(Guid.empty)) return 'System';
  const grantorName = individuals.find(
    individual => individual.userId.isEqualTo(permission.grantorUserId))?.getFullName();
  const isLoadingGrantor = loadingIndividualUserIds.some(id => id.isEqualTo(permission.grantorUserId));
  if (isLoadingGrantor) return '(Loading...)';
  return grantorName ?? 'Unknown User';
};

export function getProposalName(
  resource: ProposalAccess,
  loadingProposalIds: Guid[],
  allProposalErrors: ProposalError[],
  barebonesProposals: BarebonesProposalInfoAPIResponse[]
): string {
  return loadName(
    resource.proposalId, {
    isLoading: (id) => loadingProposalIds.some(loadingId => loadingId.isEqualTo(id)),
    error: (id) => allProposalErrors.find(error => error.id.isEqualTo(id))?.error?.message || null,
    pick: (id) => barebonesProposals?.find(proposal => proposal?.id === id.value)?.name
  }, "Unknown Proposal");
}

export function getWorkAgreementName(
  resource: WorkAgreementAccess,
  loadingWorkAgreementIds: Guid[],
  allWorkAgreementErrors: WorkAgreementError[],
  barebonesWorkAgreements: BarebonesWorkAgreementInfoAPIResponse[]
): string {
  return loadName(
    resource.workId, {
    isLoading: (id) => loadingWorkAgreementIds.some(loadingId => loadingId.isEqualTo(id)),
    error: (id) => allWorkAgreementErrors.find(error => error.id.isEqualTo(id))?.error?.message || null,
    pick: (id) => barebonesWorkAgreements?.find(wa => wa?.id === id.value)?.name
  }, "Unknown Work Agreement");
}

export function getEntityAgreementName(
  resource: EntityAgreementAccess,
  loadingEntityAgreementIds: Guid[],
  allEntityAgreementErrors: EntityAgreementError[],
  entityAgreements: EntityAgreement[]
): string {
  return loadName(
    resource.entityAgreementId, {
    isLoading: (id) => loadingEntityAgreementIds.some(loadingId => loadingId.isEqualTo(id)),
    error: (id) => allEntityAgreementErrors.find(error => error.id.isEqualTo(id))?.error?.message || null,
    pick: (id) => entityAgreements?.find(ea => ea?.id.isEqualTo(id))?.entityName?.value
  }, "Unknown Entity Agreement");
}

export function getDocumentName(
  resource: DocumentAccess,
  loadingDocumentIds: Guid[],
  allDocumentErrors: DocumentError[],
  documents: Array<Document | UnavailableDocument>
): string {
  return loadName(
    resource.documentId, {
    isLoading: (id) => loadingDocumentIds.some(loadingId => loadingId.isEqualTo(id)),
    error: (id) => allDocumentErrors.find(error => error.id.isEqualTo(id))?.error || null,
    pick: (id) => {
      const doc = documents.find(doc => doc.id.isEqualTo(id));
      if (!doc) return undefined;
      if (doc instanceof Document) {
        return doc.name?.value;
      }
      return "Unavailable Document";
    }
  }, "Unknown Document");
}

export function getForumName(
  resource: ForumAccess,
  loadingForumIds: Guid[],
  allForumErrors: ForumError[],
  forums: Forum[]
): string {
  return loadName(
    resource.forumId, {
    isLoading: (id) => loadingForumIds.some(loadingId => loadingId.isEqualTo(id)),
    error: (id) => allForumErrors.find(error => error.id.isEqualTo(id))?.error || null,
    pick: (id) => forums.find(forum => forum?.id?.isEqualTo(id))?.name
  }, "Unknown Forum");
}

export function getLegalEntityName(
  session: Session,
  resource: LegalEntityAccess,
  legalEntities: UserEntityInfo[]
): string {
  const legalEntity = legalEntities.find(entity =>
    entity.entityId.isEqualTo(resource.legalEntityId)
  );
  return legalEntity?.entityName.value ?? session.currentEntity.entityName.value ?? "Unknown Entity";
}

export function getControlMeta(
  resource: Resource,
  controlType: MetaControlType
): ControlMeta | null {
  if ('accessLevel' in resource && controlType === MetaControlType.AccessLevel) {
    return {
      type: MetaControlType.AccessLevel,
      label: 'Access Level',
      options: enumKeys(AccessLevel),
      value: enumKeyOf(AccessLevel, resource.accessLevel)
    };
  }
  if ('scope' in resource && controlType === MetaControlType.Scope) {
    return {
      type: MetaControlType.Scope,
      label: 'Scope',
      options: enumKeys(Scope),
      value: enumKeyOf(Scope, resource.scope)
    };
  }
  if ('accessType' in resource && controlType === MetaControlType.AccessType) {
    const en = AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
    if (!en) return null;
    return {
      type: MetaControlType.AccessType,
      label: 'Access Type',
      options: enumKeys(en),
      value: enumKeyOf(en, resource.accessType)
    };
  }
  return null;
}

export const toCardMeta = (
  permission: Permission,
  key: string,
  control: ControlMeta | null
): CardMeta => ({
  key,
  permission,
  resource: permission.resource,
  isVirtual: !permission.id,
  control
});

export const toTemplateCardMeta = (
  resource: Resource,
  key: string,
  control: ControlMeta | null
): CardMeta => ({
  key,
  resource,
  control
});

export function clearPermissionFilters(search: string): string {
  const params = new URLSearchParams(search);
  const KEYS = [
    "granteeUserId",
    "granteeUserIds",
    "grantorUserId",
    "resource",
    "minCreatedDate",
    "maxCreatedDate",
    "minDisabledDate",
    "maxDisabledDate",
    "categories",
    "category",
    "accessType",
    "childAccessTypes",
    "hasParent",
    "documentIds",
    "proposalIds",
    "workAgreementIds"
  ];
  for (const k of KEYS) params.delete(k);
  return params.toString();
}

export function childResourceConstructors(): ReadonlyArray<ResourceConstructor> {
  const ChildTypeToConstructor: ChildResourceConstructorMap = {
    DocumentAccess,
    EntityAgreementAccess,
    ForumAccess,
    ProposalAccess,
    WorkAgreementAccess,
  } as const;
  return (Object.keys(ChildResourceType).filter(k => Number.isNaN(Number(k))) as Array<keyof typeof ChildResourceType>)
    .map(k => ChildTypeToConstructor[k]);
}

export const columnIdForTopLevel = (Ctor: ResourceConstructor) => {
  if (Ctor === DocumentContentsAccess) {
    return 'group:DocumentContents';
  } else if (Ctor === LegalEntityAccess) {
    return 'group:LegalEntities';
  } else {
    return `rt:${Ctor.name}`;
  }
};

export function sumPermissionCounts(resp?: PaginatedResponse<PermissionsByGranteeId> | undefined) {
  const totals = new Map<string, number>();
  for (const row of resp?.data ?? []) {
    for (const [k, v] of Object.entries(row.permissionCounts ?? {})) {
      totals.set(k, (totals.get(k) ?? 0) + (v ?? 0));
    }
  }
  return totals;
}

export function computeTopLevelVisibility(
  topLevelResources: ResourceConstructor[],
  totals: Map<string, number>,
  { hasParent, hasAdditionalFilters }: { hasParent: boolean; hasAdditionalFilters: boolean }
) {
  const next: Record<string, boolean> = {};
  const suppressTop = hasParent || hasAdditionalFilters;
  for (const Ctor of topLevelResources) {
    const id = columnIdForTopLevel(Ctor);
    const total = totals.get(Ctor.name) ?? 0;
    next[id] = suppressTop ? false : total > 0;
  }
  if (!suppressTop) next['group:DocumentContents'] = true;
  return next;
}

export function computeChildVisibility(childTypesInData: ResourceConstructor[], allow: boolean) {
  const CHILD_ID: Record<string, string> = {
    ProposalAccess: 'group:Proposal',
    WorkAgreementAccess: 'group:WorkAgreement',
    LegalEntityAccess: 'group:LegalEntities',
    DocumentContentsAccess: 'group:DocumentContents',
    DocumentAccess: 'rt:DocumentAccess'
  };
  const next: Record<string, boolean> = {};
  for (const Ctor of childTypesInData) {
    const id = CHILD_ID[Ctor.name] ?? `rt:${Ctor.name}`;
    next[id] = allow;
  }
  return next;
}

export function clampByCategory(
  vis: Record<string, boolean>,
  headersById: Map<string, string>,
  activeCategory: CategoryLabel | ''
) {
  if (!activeCategory) return vis;
  const { regex } = CATEGORY_OPTIONS.find(o => o.label === activeCategory)!;
  const next: Record<string, boolean> = {};
  for (const [id, allowed] of Object.entries(vis)) {
    if (id === 'grantee') {
      next[id] = true;
      continue;
    }
    if (!allowed) continue;
    const header = headersById.get(id) ?? '';
    next[id] = regex.test(header) || regex.test(id);
  }
  return next;
}

export const isEqualVisibility = (a: Record<string, boolean>, b: Record<string, boolean>) =>
  Object.keys(a).length === Object.keys(b).length &&
  Object.entries(a).every(([k, v]) => b[k] === v);

export const isPermissionsRow = (r: TableRow | null): r is PermissionsByGranteeId =>
  (r as PermissionsByGranteeId).granteeUserId !== undefined;

export const isTemplateRow = (r: TableRow | null): r is PermissionTemplate =>
  (r as PermissionTemplate).name !== undefined && (r as PermissionsByGranteeId).granteeUserId === undefined;
